import { AfterViewInit, Directive, ElementRef, HostBinding, HostListener, inject, input, output, signal } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export interface FileHandle {
    file: File;
    url: SafeUrl;
}

@Directive({
    selector: '[appUploadDrop]',
})
export class DragDropDirective implements AfterViewInit {
    private readonly sanitizer = inject(DomSanitizer);
    private el = inject(ElementRef);

    public readonly files = output<FileHandle[]>();
    public readonly hoverBackgroundColor = input<string | null>(null);
    public readonly hoverBorderColor = input<string | null>(null);
    public readonly enabledUpload = input(true);

    @HostBinding('style.background') private background;
    @HostBinding('style.border-color') private borderColor;

    private readonly initBackgroundColor = signal('');
    private readonly initBorderColor = signal('transparent');
    private dropPlaceholder = signal<HTMLDivElement | null>(null);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.initBackgroundColor.set(this.background);
        this.initBorderColor.set(this.borderColor);
    }

    ngAfterViewInit(): void {
        this.dropPlaceholder.set(this.el.nativeElement.querySelector('.drop-placeholder'));

        if (!!this.dropPlaceholder()) {
            this.dropPlaceholder.update((placeholder) => {
                placeholder.style.opacity = '0';
                placeholder.style.zIndex = '0';
                return placeholder;
            });
        }
    }

    @HostListener('dragover', ['$event'])
    onDragOver(evt: DragEvent): void {
        evt.preventDefault();
        evt.stopPropagation();
        if (!this.enabledUpload()) {
            return;
        }

        if (!!this.hoverBackgroundColor()) {
            this.background = this.hoverBackgroundColor();
        }

        if (!!this.hoverBorderColor()) {
            this.borderColor = this.hoverBorderColor();
        }

        if (!!this.dropPlaceholder()) {
            this.dropPlaceholder.update((placeholder) => {
                placeholder.style.opacity = '1';
                placeholder.style.zIndex = '10';
                return placeholder;
            });
        }
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(evt: DragEvent): void {
        evt.preventDefault();
        evt.stopPropagation();
        if (!this.enabledUpload()) {
            return;
        }

        if (!!this.hoverBackgroundColor()) {
            this.background = this.initBackgroundColor();
        }

        if (!!this.hoverBorderColor()) {
            this.borderColor = this.initBorderColor();
        }

        if (!!this.dropPlaceholder()) {
            this.dropPlaceholder.update((placeholder) => {
                placeholder.style.opacity = '0';
                placeholder.style.zIndex = '0';
                return placeholder;
            });
        }
    }

    @HostListener('drop', ['$event'])
    onDrop(evt: DragEvent): void {
        evt.preventDefault();
        evt.stopPropagation();
        if (!this.enabledUpload()) {
            return;
        }

        if (!!this.hoverBackgroundColor()) {
            this.background = this.initBackgroundColor();
        }

        if (!!this.hoverBorderColor()) {
            this.borderColor = this.initBorderColor();
        }

        if (!!this.dropPlaceholder()) {
            this.dropPlaceholder.update((placeholder) => {
                placeholder.style.opacity = '0';
                placeholder.style.zIndex = '0';
                return placeholder;
            });
        }

        const files: FileHandle[] = [];

        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < evt.dataTransfer.files.length; i++) {
            const file = evt.dataTransfer.files[i];
            const url = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file));
            files.push({ file, url });
        }

        if (files.length > 0) {
            this.files.emit(files);
        }
    }
}
